<template>
  <div class="fixed-sn">
    <div v-if="brandingReady" class="wrapper w-full max-w-none">
      <!-- NAVBAR -->
      <Nav :toggle-sidebar="toggleSidebar" :logo-src="brandLogo" />

      <!-- OVERLAY: mobile only -->
      <Overlay v-if="isSidebarActive" class="lg:hidden" @click="toggleSidebar" />

      <!-- SIDEBAR -->
      <Sidebar :is-active="isSidebarActive" :toggle-sidebar="toggleSidebar" />

      <!-- MAIN CONTENT -->
      <main class="pt-22.5 pb-4 lg:pt-20.5 lg:ml-65 px-4">
        <nav aria-label="breadcrumb" class="my-4">
          <ol class="flex items-center text-sm lg:text-base text-primary-font font-bold leading-none">
            <li v-for="(data, index) in globalBreadcrumb" :key="index" class="flex items-center">
              <span v-if="index !== 0" class="mx-2 text-primary-font">/</span>

              <component
                :is="data.isSub ? 'router-link' : 'span'"
                :to="data.route"
                class="hover:text-primary transition"
              >
                {{ $t(data.displayName) }}
              </component>
            </li>
          </ol>
        </nav>

        <router-view />
      </main>
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent } from 'vue';
import { getModule } from 'vuex-module-decorators';

import Nav from '@/components/Nav.vue';
import Sidebar from '@/components/Sidebar.vue';
import Overlay from '@/components/Overlay.vue';
import MenuData from '@/data/Menu';

import SettingModule from '@/store/modules/Setting';
import WhiteLabelModule from '@/store/modules/WhiteLabel';
import store from '@/store';

const DEFAULT_COLOR = {
  primaryBackground: '#f2f3f4',
  primaryColor: '#0d8c67',
  primaryFont: '#333',
  secondaryBackground: '#ffffff',
  secondaryColor: '#e7dc4e',
  secondaryFont: '#fff',
  thirdFont: '#5c676d'
};

const CSS_VAR_MAP: Record<string, string> = {
  primaryColor: '--color-primary',
  secondaryColor: '--color-secondary',
  primaryFont: '--color-primary-font',
  secondaryFont: '--color-secondary-font',
  thirdFont: '--color-third-font',
  primaryBackground: '--color-primary-background',
  secondaryBackground: '--color-secondary-background'
};

function applyThemeColor(color: Record<string, any>) {
  Object.entries(color).forEach(([key, value]) => {
    const cssVar = CSS_VAR_MAP[key];
    if (!cssVar || typeof value !== 'string') return;

    document.documentElement.style.setProperty(cssVar, value);
  });
}

function preloadImage(src?: string): Promise<void> {
  return new Promise(resolve => {
    if (!src) {
      resolve();
      return;
    }

    const img = new Image();

    img.onload = () => resolve();
    img.onerror = () => resolve();
    img.src = src;
  });
}

export default defineComponent({
  name: 'DefaultLayout',
  // eslint-disable-next-line vue/no-reserved-component-names
  components: { Nav, Sidebar, Overlay },

  data() {
    return {
      SettingInstance: getModule(SettingModule, store),
      WhiteLabelInstance: getModule(WhiteLabelModule, store),

      globalBreadcrumb: [] as any[],
      isSidebarActive: false,
      brandingReady: false,
      brandLogo: '' as string
    };
  },

  watch: {
    $route: {
      immediate: true,
      handler() {
        this.renderGlobalBreadcrumb();
      }
    }
  },

  async created() {
    await this.prepareBranding();
  },

  mounted() {
    if (window.innerWidth >= 1024) {
      this.isSidebarActive = false;
    }
  },

  methods: {
    async prepareBranding() {
      let settingColor: any = null;
      let whiteLabelSetting: any = null;

      try {
        const settingRes: any = await this.SettingInstance.GetData();

        settingColor =
          settingRes?.data?.color ||
          settingRes?.data?.data?.color ||
          (this.SettingInstance as any)?.data?.color ||
          (this.SettingInstance as any)?.setting?.color ||
          null;
      } catch (err) {
        console.error('GetData failed in DefaultLayout:', err);
      }

      try {
        whiteLabelSetting = await this.WhiteLabelInstance.FetchSettingImage();
      } catch (err) {
        console.error('FetchSettingImage failed in DefaultLayout:', err);
      }

      const faviconEl = document.getElementById('favicon') as HTMLLinkElement | null;
      const href =
        (whiteLabelSetting?.agent && typeof whiteLabelSetting.agent === 'object'
          ? whiteLabelSetting.agent.favicon
          : '') ||
        whiteLabelSetting?.image?.agent?.favicon;

      if (faviconEl && href) {
        faviconEl.setAttribute('href', href);
      }

      const themeColor = {
        ...DEFAULT_COLOR,
        ...(whiteLabelSetting?.agentTheme || {}),
        ...(settingColor || {})
      };

      applyThemeColor(themeColor);

      const logo =
        (whiteLabelSetting?.agent && typeof whiteLabelSetting.agent === 'object'
          ? whiteLabelSetting.agent.logo
          : '') ||
        whiteLabelSetting?.image?.agent?.logo ||
        '';
      await preloadImage(logo);

      this.brandLogo = logo;
      this.brandingReady = true;
    },

    renderGlobalBreadcrumb() {
      this.globalBreadcrumb = [];
      const { menuList } = MenuData as any;
      let current: any;

      menuList.every((element: any) => {
        if (element.route === this.$route.path) {
          current = { ...element };
          return false;
        }

        if ('child' in element) {
          const child = element.child.find((menu: any) => menu.route === this.$route.path);

          if (child) {
            current = child;
            return false;
          }
        }

        return true;
      });

      if (current && 'parentName' in current) {
        const type = typeof current.parentName;

        if (type === 'string') {
          const parent: any = (menuList as any[]).find((menu: any) => menu.name === current.parentName);

          if (parent) this.globalBreadcrumb.push(parent);
        } else if (type === 'object' && Array.isArray(current.parentName)) {
          const parent: any = (menuList as any[]).find((menu: any) => menu.name === current.parentName[0]);
          const sub: any = parent?.child?.find((menu: any) => menu.name === current.parentName[1]);

          if (parent && sub) {
            sub.isSub = true;
            this.globalBreadcrumb.push(parent, sub);
          }
        }
      }

      if (current) {
        this.globalBreadcrumb.push(current);
      }
    },

    toggleSidebar() {
      this.isSidebarActive = !this.isSidebarActive;
    }
  }
});
</script>
